import {
	AlertBar,
	Dialog,
	Form,
	FormattedText,
	FormItem,
	TextInput,
	TextInputReadOnly,
	Toolbar,
	useConfirmationDialog,
	ValidationError
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	ReadonlyRefObject,
	useCallback,
	useEffect,
	useState
} from "react";
import styles from "./ResultMapperDialog.module.less";
import {
	convertServerErrors,
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import {
	doesNotExceedLength,
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	GlobalSearchMappingStepConfigurationSaveModel,
	MappingStepConfigurationSaveModel,
	MappingStepConfigurationWithAiSaveModel,
	MappingStepConfigurationWithGlobalSearchSaveModel,
	IntegrationFlowRequestResultModel,
	IntegrationFlowValidationResultGlobalSearchSampleModel,
	IntegrationFlowValidationResultAppSampleModel,
	ResultMapperColorConditionModel,
	ResultMapperStyledFieldSaveModel,
	AiMappingStepConfigurationSaveModel,
	IntegrationFlowValidationResultAiSampleModel
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import {
	useSynchronizedRef,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import {
	getRequestDescription,
	getRequestStepTag,
	isAiMappingStep,
	isGlobalSearchMappingStep,
	AppMappingStepConfigurationFormData,
	MappingStepConfigurationWithAppFormData,
	ResultMapperColorConditionModelFormData,
	ResultMapperStyledFieldFormData,
	PerItemRequestStepConfiguration,
	RequestStepConfiguration
} from "./common";
import {
	EMPTY_ARRAY,
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { Step } from "./Step";
import { Guid } from "client_legacy/ndaw/guid";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import { IObjectValidatorBuilder, IValidationConfiguration } from "client/Validation/builder";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { StepResponse } from "./StepResponse";
import { ClientValidationErrorModel } from "client/Api/common";
import { DotPathWildcardPattern } from "client/Misc/dotPathWildcardPattern";
import { TimeZone } from "client/DateAndTime/timezones";
import { GlobalSearchForm } from "./GlobalSearchForm";
import { InputWithSpecialSyntax } from "./InputWithSpecialSyntax";
import { useInfoPlaceHoldersDialog } from "./InfoPlaceHoldersDialog";
import { AppForm } from "./AppForm";
import { AiForm } from "./AiForm";

export type ResultMapperDialogProps = {
	stepIndex: number;
	initialData: MappingStepConfigurationSaveModel;
	steps: readonly ObjectWithClientId<RequestStepConfiguration | PerItemRequestStepConfiguration>[];
	stepResults: Map<Guid, IntegrationFlowRequestResultModel>;
	serverErrors: readonly ClientValidationErrorModel[];
	stepWithErrorIds: ReadonlySet<Guid>;
	globalSearchSample: OptionalModel<IntegrationFlowValidationResultGlobalSearchSampleModel> | null;
	appSample: OptionalModel<IntegrationFlowValidationResultAppSampleModel> | null;
	aiSample: OptionalModel<IntegrationFlowValidationResultAiSampleModel> | null;
	isReadOnly?: boolean;
	onDone(step: MappingStepConfigurationSaveModel): void;
	onClose(): void;
	onValidate(newStepData: MappingStepConfigurationSaveModel): Promise<void>;
};

export const ResultMapperDialog = (props: ResultMapperDialogProps): ReactElement => {
	const {
		stepIndex,
		initialData,
		steps,
		isReadOnly = false,
		globalSearchSample,
		appSample,
		aiSample,
		serverErrors,
		stepResults,
		stepWithErrorIds,
		onClose,
		onDone,
		onValidate
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [shouldShowMapFieldsInfo, setShouldShowMapFieldsInfo] = useState<boolean>(true);
	const [formData, setFormData] = useState<MappingStepConfigurationFormData>(
		() => convertToFormData(initialData)
	);
	const [isTriedToValidate, setIsTriedToValidate] = useState<boolean>(false);

	const [closeConfirmationDialog, openCloseConfirmationDialog] = useConfirmationDialog();
	const userTimezone: TimeZone = useUserTimezone();
	const [infoPlaceHoldersDialog, openInfoPlaceHoldersDialog] = useInfoPlaceHoldersDialog();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const formHandler: IFormHandler<MappingStepConfigurationFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const stableFormHandler: ReadonlyRefObject<IFormHandler<MappingStepConfigurationFormData>> = useSynchronizedRef(formHandler);

	const setServerErrorsHandler = useCallback(
		(errors: readonly ClientValidationErrorModel[]): void => {
			stableFormHandler.current
				.validation
				.setServerErrors(
					convertServerErrors(
						errors,
						[
							{
								pattern: new DotPathWildcardPattern(`Steps[${stepIndex}].Mapping.**`),
								replacement: "{0}",
							}
						]
					)
				);
		},
		[
			stableFormHandler,
			stepIndex
		]
	);

	const validateHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy || isReadOnly) {
				return;
			}

			setIsTriedToValidate(true);
			setIsBusy(true);
			const validationResult: InputValidationResult = await formHandler
				.validation
				.validate(globalCancellation);

			if (validationResult.kind === InputValidationResultKind.Invalid) {
				setIsBusy(false);
				return;
			}

			await onValidate(convertToSaveModel(formData));

			setIsBusy(false);
		},
		[
			formData,
			formHandler.validation,
			globalCancellation,
			isBusy,
			isReadOnly,
			onValidate
		]
	);

	const doneHandler = useCallback(
		async(): Promise<void> => {
			if (!isTriedToValidate || isReadOnly) {
				return;
			}

			const validationResult: InputValidationResult = await formHandler
				.validation
				.validate(globalCancellation);

			if (validationResult.kind !== InputValidationResultKind.Valid) {
				return;
			}

			onDone(convertToSaveModel(formData));
			onClose();
		},
		[
			formData,
			isTriedToValidate,
			onClose,
			onDone,
			formHandler.validation,
			globalCancellation,
			isReadOnly
		]
	);

	const closeHandler = useCallback(
		async (): Promise<void> => {
			const hasChanges: boolean = !deepComparison(
				formData,
				convertToFormData(initialData)
			);

			if (hasChanges) {
				const confirmation: TryResult<void> = await openCloseConfirmationDialog({
					title: localize("_AreYouSureYouWantToLeaveThisPage"),
					confirmText: localize("_DiscardChanges"),
					children: localize("_YouHaveUnsavedChanges"),
					trackingId: "discard-integration-builder-result-mapper-form-confirmation-dialog"
				});

				if (!confirmation.wasSuccessful) {
					return;
				}
			}

			onClose();
		},
		[
			formData,
			initialData,
			onClose,
			openCloseConfirmationDialog
		]
	);

	useEffect(
		(): void => {
			// NOTE: Due to a bug in our validation infrastructure if we set server errors
			// directly after user opens the dialog, they will be overwritten by some queue update
			setTimeout(
				() => setServerErrorsHandler(serverErrors),
				1
			);
		},
		[
			serverErrors,
			setServerErrorsHandler
		]
	);

	return (
		<>
			<Dialog
				trackingId="integration-result-mapper-dialog"
				variant="left-slider"
				onClose={closeHandler}
				hasBusyIndicator={isBusy}
			>
				<Dialog.Header
					title={localize("_ResultMapper")}
					onClose={closeHandler}
					hasBorder
					onShowInfo={() => openInfoPlaceHoldersDialog({})}
				/>
				<Dialog.Body>
					<div className={styles.container}>
						<div className={styles.leftSide}>
							<h4 className={styles.title}>{localize("_RequestResults")}</h4>
							{steps.length > 0 && (
								<ul>
									{steps.map(
										(x, index) => {
											const {
												name
											} = x;

											const clientId: Guid = getClientId(x);
											const hasError: boolean = stepWithErrorIds.has(clientId);
											const response: IntegrationFlowRequestResultModel | undefined = stepResults.get(clientId);
											const trackingId = generateTrackingId("request-step", `|${index}`);

											return (
												<li
													key={clientId}
													className={styles.listItem}
												>
													<Step
														{...(isNotNil(response)
															? {
																isCollapsible: true,
																initialIsCollapsed: index !== steps.length - 1
															}
															: {
																isCollapsible: false
															}
														)}
														title={name}
														description={getRequestDescription(x)}
														trackingId={trackingId}
														tagConfiguration={getRequestStepTag(hasError, clientId, stepResults)}
													>
														{response && (
															<StepResponse
																response={response}
															/>
														)}
													</Step>
												</li>
											);
										}
									)}
								</ul>
							)}
						</div>
						<div className={styles.rightSide}>
							<h4 className={styles.title}>
								{localize("_ResultMapper")}
							</h4>
							<div className={styles.mapperContainer}>
								<Form onSubmit="NONE">
									<FormItem
										label={localize("_ResultCollection")}
									>
										{isReadOnly
											? (
												<TextInputReadOnly
													{...formHandler.for("resultCollectionExpression").attachToTextInput()}
												/>
											)
											: (
												<>
													<InputWithSpecialSyntax kind="expression">
														<TextInput
															{...formHandler.for("resultCollectionExpression").attachToTextInput()}
															placeholder={localize("_EnterResultCollection")}
														/>
													</InputWithSpecialSyntax>
													<ValidationError {...formHandler.for("resultCollectionExpression").attachToValidationError()} />
												</>
											)
										}
									</FormItem>
									<div className={styles.infoText}>
										<FormattedText
											text={localize("_ThisIsTheCollectionOfFieldsThatCanBeUsedInfo")}
											thingsToWrap={[
												{
													blockId: "Step",
													wrap: (x) => <i>{x}</i>
												}
											]}
										/>
									</div>
									<hr className={styles.divider}/>
									{shouldShowMapFieldsInfo && (
										<div className={styles.alert}>
											<AlertBar
												isInline
												kind="info"
												isDismissable
												trackingId="request-collection-info"
												onDismiss={() => setShouldShowMapFieldsInfo(false)}
											>
												<FormattedText
													text={localize("_YouCanSimplyMapFieldsOfTheResultCollectionInfo")}
													thingsToWrap={[
														{
															blockId: "Format",
															wrap: x => <i>{x}</i>
														},
														{
															blockId: "ObjectName",
															wrap: x => <i>{x}</i>
														}
													]}
												/>
											</AlertBar>
										</div>
									)}

									{formHandler.whenType(
										isGlobalSearchMappingStepFormData,
										x => {
											const attachable: IObjectInputAttachable<GlobalSearchMappingStepConfigurationSaveModel> = x.for("globalSearch");

											return (
												<GlobalSearchForm
													attachable={attachable}
													isReadOnly={isReadOnly}
													globalSearchSample={globalSearchSample}
													timeZone={userTimezone}
												/>
											);
										}
									)}
									{formHandler.whenType(
										isAppMappingStepFormData,
										x => {
											const attachable: IObjectInputAttachable<AppMappingStepConfigurationFormData> = x.for("app");

											return (
												<AppForm
													attachable={attachable}
													isReadOnly={isReadOnly}
													appSample={appSample}
													timeZone={userTimezone}
												/>
											);
										}
									)}
									{formHandler.whenType(
										isAiMappingStepFormData,
										x => {
											const attachable: IObjectInputAttachable<AiMappingStepConfigurationSaveModel> = x.for("ai");

											return (
												<AiForm
													attachable={attachable}
													isReadOnly={isReadOnly}
													aiSample={aiSample}
													timeZone={userTimezone}
												/>
											);
										}
									)}
								</Form>
							</div>
						</div>
					</div>
				</Dialog.Body>
				<Dialog.Footer>
					<Toolbar>
						{!isReadOnly && (
							<>
								<Toolbar.Button
									kind="primary"
									text={localize("_Validate")}
									onClick={validateHandler}
									trackingId="validate"
								/>
								<Toolbar.Button
									kind="primary"
									text={localize("_Done")}
									onClick={doneHandler}
									trackingId="done"
									isDisabled={!isTriedToValidate}
								/>
							</>
						)}
						<Toolbar.Button
							kind="secondary"
							text={localize(isReadOnly
								? "_Close"
								: "_Cancel"
							)}
							trackingId="cancel"
							onClick={onClose}
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{closeConfirmationDialog}
			{infoPlaceHoldersDialog}
		</>
	);
};
ResultMapperDialog.displayName = "ResultMapperDialog";

type MappingStepConfigurationFormData = MappingStepConfigurationWithGlobalSearchSaveModel
	| MappingStepConfigurationWithAiSaveModel
	| MappingStepConfigurationWithAppFormData;

function convertResultMapperStyledFieldToFormData(
	model: ResultMapperStyledFieldSaveModel | null
): ResultMapperStyledFieldFormData {
	if (model === null) {
		return {
			expression: "",
			colors: EMPTY_ARRAY,
			shouldIncludeColors: false
		};
	}

	return {
		expression: model.expression,
		shouldIncludeColors: isNotNil(model.colors),
		colors: model.colors
			? model.colors
				.map(
					x => setClientId(x)
				)
			: []
	};
};

const convertToFormData = (
	model: MappingStepConfigurationSaveModel
): MappingStepConfigurationFormData => {
	if (isGlobalSearchMappingStep(model) || isAiMappingStep(model)) {
		return model;
	}

	const { app } = model;

	return {
		resultCollectionExpression: model.resultCollectionExpression,
		app: {
			linkExpression: app.linkExpression,
			title: convertResultMapperStyledFieldToFormData(app.title),
			date: convertResultMapperStyledFieldToFormData(app.date),
			subtitle1: convertResultMapperStyledFieldToFormData(app.subtitle1),
			subtitle2: convertResultMapperStyledFieldToFormData(app.subtitle2),
			subtitle3: convertResultMapperStyledFieldToFormData(app.subtitle3),
			description: convertResultMapperStyledFieldToFormData(app.description)
		}
	};
};

function convertResultMapperStyledFieldToSaveModel(
	model: ResultMapperStyledFieldFormData
) : ResultMapperStyledFieldSaveModel | null {
	if (!model.expression && !model.shouldIncludeColors) {
		return null;
	}

	return {
		expression: model.expression,
		colors: model.shouldIncludeColors
			? convertResultMapperColorConditionsToSaveModel(model.colors)
			: null
	};
};

function convertResultMapperColorConditionsToSaveModel(
	models: readonly ResultMapperColorConditionModelFormData[]
): readonly ResultMapperColorConditionModel[] {
	return models.map(
		x => ({
			conditionExpression: x.conditionExpression,
			color: ensureNotNil(x.color)
		})
	);
}

const convertToSaveModel = (
	model: MappingStepConfigurationFormData
): MappingStepConfigurationSaveModel => {
	if (isGlobalSearchMappingStepFormData(model) || isAiMappingStepFormData(model)) {
		return model;
	}

	const { app } = model;

	return {
		resultCollectionExpression: model.resultCollectionExpression,
		app: {
			linkExpression: model.app.linkExpression,
			title: convertResultMapperStyledFieldToSaveModel(app.title)
				?? {
					expression: "",
					colors: null
				},
			date: convertResultMapperStyledFieldToSaveModel(app.date),
			subtitle1: convertResultMapperStyledFieldToSaveModel(app.subtitle1),
			subtitle2: convertResultMapperStyledFieldToSaveModel(app.subtitle2),
			subtitle3: convertResultMapperStyledFieldToSaveModel(app.subtitle3),
			description: convertResultMapperStyledFieldToSaveModel(app.description)
		}
	};
};

const isAppMappingStepFormData = (
	mappingStep: MappingStepConfigurationFormData
): mappingStep is MappingStepConfigurationWithAppFormData => {
	return isNotNil((mappingStep as MappingStepConfigurationWithAppFormData).app);
};

const isGlobalSearchMappingStepFormData = (
	mappingStep: MappingStepConfigurationFormData
): mappingStep is MappingStepConfigurationWithGlobalSearchSaveModel => {
	return isNotNil((mappingStep as MappingStepConfigurationWithGlobalSearchSaveModel).globalSearch);
};

const isAiMappingStepFormData = (
	mappingStep: MappingStepConfigurationFormData
): mappingStep is MappingStepConfigurationWithAiSaveModel => {
	return isNotNil((mappingStep as MappingStepConfigurationWithAiSaveModel).ai);
};

const validationConfiguration = createValidationConfiguration<MappingStepConfigurationFormData>();

const globalSearchValidationConfiguration: IObjectValidatorBuilder<GlobalSearchMappingStepConfigurationSaveModel> = validationConfiguration
	.whenType(isGlobalSearchMappingStepFormData)
	.forObject("globalSearch");

validationConfiguration.forScalar("resultCollectionExpression")
	.add(
		doesNotExceedLength(255)
	);

globalSearchValidationConfiguration.forScalar("titleExpression")
	.add(
		isNotEmpty(),
		doesNotExceedLength(2_500)
	);

globalSearchValidationConfiguration.forScalar("linkExpression")
	.add(
		isNotEmpty(),
		doesNotExceedLength(2_500)
	);

const appValidationConfiguration: IObjectValidatorBuilder<AppMappingStepConfigurationFormData> = validationConfiguration
	.whenType(isAppMappingStepFormData)
	.forObject("app");

appValidationConfiguration.forObject("title")
	.addValidators(
		createStyledFieldValidationConfiguration(true)
	);

appValidationConfiguration.forScalar("linkExpression")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(2_500)
	);

appValidationConfiguration.forObject("subtitle1")
	.addValidators(
		createStyledFieldValidationConfiguration(false)
	);

appValidationConfiguration.forObject("subtitle2")
	.addValidators(
		createStyledFieldValidationConfiguration(false)
	);

appValidationConfiguration.forObject("subtitle3")
	.addValidators(
		createStyledFieldValidationConfiguration(false)
	);
appValidationConfiguration.forObject("date")
	.addValidators(
		createStyledFieldValidationConfiguration(false)
	);

appValidationConfiguration.forObject("description")
	.addValidators(
		createStyledFieldValidationConfiguration(false)
	);

function createStyledFieldValidationConfiguration(isFieldRequired: boolean): IValidationConfiguration<ResultMapperStyledFieldFormData> {
	const result: IValidationConfiguration<ResultMapperStyledFieldFormData> = createValidationConfiguration<ResultMapperStyledFieldFormData>();

	const colorConditionValidationConfiguration: IObjectValidatorBuilder<ResultMapperColorConditionModelFormData> = result
		.when(x => x.shouldIncludeColors)
		.forCollection("colors")
		.forItems(getClientId);

	colorConditionValidationConfiguration.forScalar("conditionExpression")
		.add(
			isNotNullNorUndefined(),
			isNotEmpty(),
			doesNotExceedLength(2_500)
		);

	colorConditionValidationConfiguration.forScalar("color")
		.add(
			isNotNullNorUndefined()
		);

	result.forScalar("expression")
		.add(
			doesNotExceedLength(2_500)
		);

	result
		.when(x => isFieldRequired || x.shouldIncludeColors)
		.forScalar("expression")
		.add(
			isNotNullNorUndefined(),
			isNotEmpty()
		);

	return result;
}

const aiValidationConfiguration: IObjectValidatorBuilder<AiMappingStepConfigurationSaveModel> = validationConfiguration
	.whenType(isAiMappingStepFormData)
	.forObject("ai");

aiValidationConfiguration.forScalar("contentExpression")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(2_500)
	);

aiValidationConfiguration.forScalar("idExpression")
	.add(
		doesNotExceedLength(2_500)
	);

aiValidationConfiguration.forScalar("titleExpression")
	.add(
		doesNotExceedLength(2_500)
	);

aiValidationConfiguration.forScalar("linkExpression")
	.add(
		doesNotExceedLength(2_500)
	);

aiValidationConfiguration.forScalar("dateExpression")
	.add(
		doesNotExceedLength(2_500)
	);
